import {
  OpenAPI as ConfigurationOpenAPI,
  type ReconciliationSpecCreateOperation,
} from '@festina/configuration-general-types/configuration'
import type { Optional } from '@festina-life/core'
import { useMutation } from '@tanstack/react-query'

import { fetchFromService } from '../fetchFromService'

type TriggerProps = {
  excelFile: File
}

/**
 * Converts a reconciliation-spec Excel document into a reconciliation-spec create
 * operation. The resulting operation is not persisted by the conversion service; it
 * has to be added to a workspace by the caller.
 *
 * The generated client cannot send the Excel document bytes as an octet-stream body,
 * so the request is issued manually.
 */
export const useMutateConvertExcelToReconciliationSpecCreateOperation = () =>
  useMutation({
    mutationFn: async ({ excelFile }: TriggerProps) => {
      const response = await fetchFromService({
        serviceConfig: ConfigurationOpenAPI,
        serviceName: 'configuration',
        path: '/conversion/reconciliationspec/convert-to-operation',
        method: 'POST',
        body: excelFile,
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/octet-stream',
        },
        errorMessage:
          'Failed to convert Excel to reconciliation-spec create operation',
      })

      return (await response.json()) as Optional<ReconciliationSpecCreateOperation>
    },
  })
